import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronRight, Clock3, Search, Ticket, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import BottomNav from "../components/BottomNav";
import API from "../services/api";

function Bids() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const starlineOnly = new URLSearchParams(location.search).get("type") === "starline";
  const [bids, setBids] = useState([]);
  const [query, setQuery] = useState("");
  const statusFilter = "all";
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => getTodayDate());
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    API.get(`/bets/${user.id}`)
      .then((res) => setBids(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBids([]));
  }, [navigate, user]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = viewportWidth <= 480;
  const isCompact = viewportWidth <= 390;

  const visibleBids = useMemo(
    () => (starlineOnly ? bids.filter(isStarlineBid) : bids),
    [bids, starlineOnly]
  );
  const groups = useMemo(() => groupBids(visibleBids), [visibleBids]);
  const dateScopedGroups = groups.filter((group) => !selectedDate || group.dateKey === selectedDate);
  const filteredGroups = dateScopedGroups.filter((group) => {
    const text = `${group.marketName} ${group.baseType} ${group.session} ${group.numbers.join(" ")} ${group.total} ${group.winTotal}`.toLowerCase();
    const matchesStatus = statusFilter === "all" || group.status === statusFilter;
    return matchesStatus && text.includes(query.toLowerCase());
  });
  return (
    <div style={page}>
      <header style={{ ...topbar, gridTemplateColumns: isCompact ? "40px 1fr" : topbar.gridTemplateColumns, padding: isCompact ? "0 10px" : topbar.padding }}>
        <button type="button" style={backButton} onClick={() => navigate("/dashboard")}>
          {"<"}
        </button>
        <strong>{starlineOnly ? "Starline History" : "My Bids"}</strong>
        {!isCompact && <div style={dateButton}>
          <CalendarDays size={15} />
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={dateInput} />
        </div>}
      </header>

      <main style={{ ...content, padding: isMobile ? "10px 10px 14px" : content.padding }}>

        <div style={{ ...searchBox, ...(isMobile ? compactSearchBox : null) }}>
          <Search size={16} color="#94a3b8" />
          <input
            style={{ ...input, ...(isMobile ? compactInput : null) }}
            placeholder={starlineOnly ? "Search starline bids..." : "Search market, amount..."}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div style={{ ...dateFilterRow, ...(isMobile ? compactDateFilterRow : null) }}>
          <span style={{ ...dateFilterLabel, ...(isMobile ? compactDateFilterLabel : null) }}>Showing bids for</span>
          <div style={dateActions}>
            <div style={{ ...dateFilterPill, ...(isMobile ? compactDateFilterPill : null) }}>
              <CalendarDays size={14} />
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={{ ...pillDateInput, ...(isMobile ? compactPillDateInput : null) }} />
            </div>
            {selectedDate ? (
              <button type="button" style={{ ...clearButton, ...(isMobile ? compactDateActionButton : null) }} onClick={() => setSelectedDate("")}>
                All Dates
              </button>
            ) : (
              <button type="button" style={{ ...todayButton, ...(isMobile ? compactDateActionButton : null) }} onClick={() => setSelectedDate(getTodayDate())}>
                Today
              </button>
            )}
          </div>
        </div>

        {!filteredGroups.length && (
          <section style={emptyCard}>
            <Ticket size={34} color="#cbd5e1" />
            <p>No bids found</p>
          </section>
        )}

        <div style={{ ...list, gap: isMobile ? "10px" : list.gap }}>
          {filteredGroups.map((group) => {
            const isWon = group.status === "won";
            const isLost = group.status === "lost";
            const accentColor = isWon ? "#16a34a" : isLost ? "#dc2626" : "#4f46e5";
            return (
              <article
                key={group.key}
                style={{ ...card, borderLeft: `4px solid ${accentColor}`, padding: isCompact ? "12px 10px" : isMobile ? "13px 12px" : card.padding }}
                onClick={() => setSelectedGroup(group)}
              >
                {/* Top: icon + name + BETED tag + session */}
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
                    <span style={{ width: isMobile ? 34 : 38, height: isMobile ? 34 : 38, borderRadius: 11, background: `${accentColor}18`, border: `1.5px solid ${accentColor}40`, display: "grid", placeItems: "center", flexShrink: 0 }}>
                      <Ticket size={isMobile ? 14 : 15} color={accentColor} />
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                        <strong style={{ fontSize: isMobile ? 13 : 14, fontWeight: 900, color: "#1e1b4b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "44vw" }}>{group.marketName}</strong>
                        <span style={cardBetedTag}>BETED</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#94a3b8", fontSize: 11 }}>
                        <Clock3 size={10} /> {group.time} · {group.dateTimeLabel}
                      </div>
                    </div>
                  </div>
                  <span style={{ ...sessionBadge(group.session), fontSize: 11, padding: "5px 10px" }}>{group.session}</span>
                </div>

                {/* Game type + numbers */}
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: "8px 12px", marginBottom: 10, fontSize: isMobile ? 12 : 13, color: "#374151" }}>
                  <span style={{ color: "#94a3b8", fontWeight: 700, marginRight: 6 }}>{group.baseType}</span>
                  <strong style={{ color: "#1e1b4b", wordBreak: "break-word" }}>{group.numbers.join(", ")}</strong>
                </div>

                {/* Bottom: total + status stamp + arrow */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#334155" }}>₹<strong style={{ color: "#1e1b4b", fontSize: 14 }}>{Number(group.total).toFixed(0)}</strong></span>
                    {isWon && group.winTotal > 0 ? <span style={wonAmountStamp}>🏆 Won {formatMoney(group.winTotal)}</span> : null}
                    {!isWon && !isLost ? <span style={pendingStamp}>⏳ Result Pending</span> : null}
                    {isLost ? <span style={lostStamp}>Result Out</span> : null}
                  </div>
                  <ChevronRight size={18} color="#cbd5e1" />
                </div>
              </article>
            );
          })}
        </div>

        {!!filteredGroups.length && (
          <section style={pager}>
            Page 1 of 1 ({filteredGroups.length} groups)
            <div style={pagerButtons}>
              <button type="button" style={pageButton} disabled>Previous</button>
              <button type="button" style={activePageButton}>1</button>
              <button type="button" style={pageButton} disabled>Next</button>
            </div>
          </section>
        )}
      </main>

      {selectedGroup && (
        <div style={detailOverlay} onClick={() => setSelectedGroup(null)}>
          <section style={detailSheet} onClick={(e) => e.stopPropagation()}>
            <div style={sheetHandle} />

            {/* ── BETED ticket header ── */}
            <div style={ticketHeader}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={betedBadge}>🎫 BETED</span>
                <button type="button" style={closeBtn} onClick={() => setSelectedGroup(null)}>
                  <X size={17} color="rgba(255,255,255,0.75)" />
                </button>
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, color: "#fff", marginBottom: 5 }}>{selectedGroup.marketName}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <Clock3 size={10} color="rgba(255,255,255,0.5)" />
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>{selectedGroup.time} · {selectedGroup.dateTimeLabel}</span>
              </div>
            </div>

            {/* ── Status banner ── */}
            <div style={statusBannerBox(selectedGroup.status)}>
              <span style={{ fontSize: 20 }}>
                {selectedGroup.status === "won" ? "🏆" : selectedGroup.status === "lost" ? "❌" : "⏳"}
              </span>
              <div style={{ fontSize: 13, fontWeight: 900, color: "#1e1b4b" }}>{getStatusLabel(selectedGroup.status)}</div>
            </div>

            {/* ── Game type + session pills ── */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              <span style={gameTypePill}>{selectedGroup.baseType}</span>
              <span style={sessionBadge(selectedGroup.session)}>
                {selectedGroup.isStarline ? "Time" : "Session"}: {selectedGroup.session}
              </span>
            </div>

            {/* ── Numbers list ── */}
            <div style={numSection}>
              <div style={numSectionLabel}>SELECTED NUMBERS</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {selectedGroup.items.map((item) => (
                  <div key={item.id} style={numRow}>
                    <div style={numBubble}>{item.number}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#1e1b4b" }}>₹{Number(item.amount || 0).toFixed(0)}</div>
                      <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{formatBidDateTime(item.created_at || item.date || item.bet_date)}</div>
                    </div>
                    <div style={numStatChip(item.status)}>{getStatusLabel(item.status)}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Totals band ── */}
            <div style={totalsBand}>
              <div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", fontWeight: 600, marginBottom: 4 }}>Total Bet Amount</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: "#fff" }}>{formatMoney(selectedGroup.total)}</div>
              </div>
              {selectedGroup.status === "won" && selectedGroup.winTotal > 0 && (
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "#86efac", fontWeight: 700, marginBottom: 4 }}>You Won</div>
                  <div style={{ fontSize: 22, fontWeight: 900, color: "#4ade80" }}>{formatMoney(selectedGroup.winTotal)}</div>
                </div>
              )}
            </div>

            <div style={{ textAlign: "center", fontSize: 11, color: "#94a3b8", paddingTop: 6 }}>
              Bet ID · <strong style={{ color: "#475569" }}>#{selectedGroup.items.map((i) => i.id).join(" · #")}</strong>
            </div>
          </section>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

function groupBids(bids) {
  const map = new Map();
  (Array.isArray(bids) ? bids : []).forEach((bid) => {
    const type = parseBetType(bid.type);
    const starline = isStarlineBid(bid);
    const slotLabel = String(bid.time_slot || "").trim();
    const minuteKey = minuteBucket(bid.created_at || bid.date || bid.bet_date);
    const key = [
      bid.market_id || bid.market_name || "market",
      type.baseType,
      starline ? slotLabel || "slot" : type.session,
      minuteKey,
      String(bid.status || "pending").toLowerCase(),
    ].join("|");

    if (!map.has(key)) {
      map.set(key, {
        key,
        isStarline: starline,
        marketName: bid.market_name || "Market",
        baseType: starline ? `STARLINE ${type.baseType}` : type.baseType,
        session: starline ? (slotLabel || "STARLINE") : type.session,
        status: String(bid.status || "pending").toLowerCase(),
        time: starline ? (slotLabel || formatBidTime(bid.created_at || bid.date)) : formatBidTime(bid.created_at || bid.date),
        total: 0,
        winTotal: 0,
        numbers: [],
        items: [],
        dateKey: toDateKey(bid.created_at || bid.date || bid.bet_date),
        dateTimeLabel: starline
          ? `${formatBidDateTime(bid.created_at || bid.date || bid.bet_date)}${slotLabel ? ` • Result time ${slotLabel}` : ""}`
          : formatBidDateTime(bid.created_at || bid.date || bid.bet_date),
      });
    }

    const group = map.get(key);
    group.total += Number(bid.amount || 0);
    group.winTotal += Number(bid.payout || 0);
    group.numbers.push(String(bid.number));
    group.items.push(bid);
  });

  return Array.from(map.values()).map((group) => ({
    ...group,
    numbers: sortNumbers(group.numbers),
    items: group.items.sort((a, b) => String(a.number).localeCompare(String(b.number), undefined, { numeric: true })),
  }));
}

function isStarlineBid(bid) {
  const text = `${bid.type || ""} ${bid.market_type || ""} ${bid.market_name || ""} ${bid.time_slot || ""}`.toLowerCase();
  return text.includes("starline") || !!bid.time_slot;
}

function parseBetType(value = "") {
  const raw = String(value || "").toLowerCase();
  const session = raw.includes("open") ? "OPEN" : raw.includes("close") ? "CLOSE" : "OPEN";
  const base = raw
    .replace(/[-_](open|close)$/i, "")
    .replace(/[-_]/g, " ")
    .trim();
  return { baseType: prettyType(base || raw || "bet").toUpperCase(), session };
}

function sortNumbers(values) {
  return [...values].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
}

function minuteBucket(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "no-time";
  date.setSeconds(0, 0);
  return date.toISOString();
}

function prettyType(value = "") {
  return String(value || "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatBidTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

function formatBidDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(value) {
  return `₹${Number(value || 0).toFixed(0)}`;
}

function getStatusLabel(status) {
  const value = String(status || "pending").toLowerCase();
  if (value === "pending") return "Bet Success - Result Pending";
  if (value === "won") return "Won";
  if (value === "lost") return "Lost";
  return prettyType(value);
}

function toDateKey(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
}

function getTodayDate() {
  return new Date().toISOString().slice(0, 10);
}

const page = { minHeight: "100vh", background: "linear-gradient(160deg,#fdf4ff 0%,#f0f4ff 100%)", paddingBottom: "74px" };
const topbar = { width: "min(900px, 100%)", height: "58px", margin: "0 auto", display: "grid", gridTemplateColumns: "44px 1fr auto", alignItems: "center", background: "linear-gradient(135deg,#1e1b4b,#312e81)", padding: "0 14px", boxSizing: "border-box", color: "#fff" };
const backButton = { border: 0, background: "rgba(255,255,255,0.12)", borderRadius: 10, width: 34, height: 34, display: "grid", placeItems: "center", fontSize: "18px", color: "#fff", cursor: "pointer" };
const dateButton = { height: "36px", display: "flex", alignItems: "center", gap: "7px", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "10px", background: "rgba(255,255,255,0.1)", color: "#fff", padding: "0 12px", fontWeight: 700, fontSize: 12 };
const content = { width: "min(900px, 100%)", margin: "0 auto", padding: "14px 14px 16px", boxSizing: "border-box" };
const dateFilterRow = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, margin: "0 0 12px", flexWrap: "wrap" };
const dateFilterLabel = { color: "#4f46e5", fontSize: 13, fontWeight: 700 };
const compactDateFilterRow = { gap: 8, marginBottom: 10 };
const compactDateFilterLabel = { fontSize: 11 };
const dateActions = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const dateFilterPill = { display: "inline-flex", alignItems: "center", gap: 8, minHeight: 36, borderRadius: 12, border: "1.5px solid #e0e7ff", background: "#fff", color: "#4f46e5", padding: "0 12px", fontWeight: 800 };
const clearButton = { minHeight: 36, borderRadius: 12, border: "1.5px solid #e5e7eb", background: "#fff", color: "#475569", padding: "0 12px", fontWeight: 700, cursor: "pointer", fontSize: 12 };
const todayButton = { minHeight: 36, borderRadius: 12, border: "1.5px solid #c7d2fe", background: "#eef2ff", color: "#4f46e5", padding: "0 12px", fontWeight: 700, cursor: "pointer", fontSize: 12 };
const dateInput = { border: 0, outline: 0, background: "transparent", color: "#334155", fontSize: 12, minWidth: 112 };
const pillDateInput = { border: 0, outline: 0, background: "transparent", color: "#4f46e5", fontSize: 13, fontWeight: 700, minWidth: 120 };
const compactDateFilterPill = { minHeight: 32, padding: "0 9px", gap: 5, fontSize: 11 };
const compactPillDateInput = { minWidth: 104, fontSize: 12 };
const compactDateActionButton = { minHeight: 32, padding: "0 10px", fontSize: 11 };
const searchBox = { height: "44px", display: "flex", alignItems: "center", gap: "10px", background: "#fff", border: "1.5px solid #e0e7ff", borderRadius: "14px", padding: "0 14px", margin: "0 0 16px", boxShadow: "0 2px 8px rgba(79,70,229,0.06)" };
const input = { flex: 1, border: 0, outline: 0, background: "transparent", fontSize: 14, color: "#0f172a" };
const compactSearchBox = { height: 38, borderRadius: 12, margin: "0 0 10px", padding: "0 10px" };
const compactInput = { fontSize: 13 };
const list = { display: "grid", gap: "14px" };
const card = { background: "#fff", border: "1.5px solid #e8eaf6", borderRadius: "18px", padding: "16px 16px 14px", cursor: "pointer", boxShadow: "0 4px 16px rgba(79,70,229,0.06)" };
const cardTop = { display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "12px" };
const marketLine = { display: "flex", alignItems: "center", gap: 10 };
const marketIcon = { width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", display: "grid", placeItems: "center" };
const marketName = { fontSize: 16, fontWeight: 900, color: "#1e1b4b" };
const timeLine = { marginTop: 3, display: "flex", alignItems: "center", gap: 5, color: "#94a3b8", fontSize: 11 };
const dateStampLine = { marginTop: 3, color: "#c4b5fd", fontSize: 11, fontWeight: 600 };
const numbersLine = { color: "#374151", margin: "0 0 14px", fontSize: 14 };
const cardBottom = { borderTop: "1px solid #f0f0ff", paddingTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", color: "#374151" };
const cardBottomTotals = { display: "grid", gap: 4, fontSize: 13, fontWeight: 700 };
const wonAmountStamp = { display: "inline-flex", alignItems: "center", width: "fit-content", minHeight: 22, padding: "0 10px", borderRadius: 999, background: "linear-gradient(135deg,#dcfce7,#bbf7d0)", color: "#166534", border: "1px solid #86efac", fontSize: 11, fontWeight: 900 };
const compactCard = { borderRadius: 14, boxShadow: "0 2px 10px rgba(79,70,229,0.05)" };
const compactCardTop = { gap: 8, marginBottom: 9 };
const compactMarketIcon = { width: 30, height: 30, borderRadius: 9 };
const compactTimeLine = { marginTop: 3, fontSize: 10, gap: 4 };
const compactDateStampLine = { marginTop: 2, fontSize: 10 };
const compactSessionBadge = { padding: "4px 8px", fontSize: 10 };
const compactNumbersLine = { margin: "0 0 10px", fontSize: "clamp(12px, 3.4vw, 13px)", lineHeight: 1.35 };
const compactCardBottom = { paddingTop: 8, fontSize: 12 };
const emptyCard = { height: "120px", display: "grid", placeItems: "center", alignContent: "center", gap: "10px", background: "#fff", border: "1.5px solid #e8eaf6", borderRadius: "16px", color: "#94a3b8" };
const pager = { marginTop: 14, background: "#fff", border: "1.5px solid #e8eaf6", borderRadius: 14, padding: 16, color: "#374151", fontSize: 13 };
const pagerButtons = { display: "flex", gap: 8, marginTop: 12 };
const pageButton = { height: 38, padding: "0 14px", border: "1.5px solid #e0e7ff", borderRadius: 10, background: "#fff", color: "#94a3b8", fontWeight: 700, fontSize: 12, cursor: "pointer" };
const activePageButton = { ...pageButton, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", borderColor: "#4f46e5" };
const sessionBadge = (session) => {
  const isTimeSlot = /\d{1,2}:\d{2}\s*(AM|PM)/i.test(String(session || ""));
  return {
    padding: "7px 14px",
    borderRadius: "999px",
    background: isTimeSlot ? "#eff6ff" : session === "CLOSE" ? "#fee2e2" : "#dcfce7",
    color: isTimeSlot ? "#4f46e5" : session === "CLOSE" ? "#dc2626" : "#15803d",
    fontSize: 12,
    fontWeight: 900,
    height: "fit-content"
  };
};
// card list extras
const cardBetedTag = { display: "inline-block", fontSize: 9, fontWeight: 900, letterSpacing: "0.07em", color: "#7c3aed", background: "#ede9fe", border: "1px solid #ddd6fe", borderRadius: 4, padding: "2px 5px", flexShrink: 0 };
const pendingStamp = { display: "inline-flex", alignItems: "center", gap: 3, minHeight: 22, padding: "0 8px", borderRadius: 999, background: "#fef9c3", color: "#92400e", border: "1px solid #fde68a", fontSize: 11, fontWeight: 700 };
const lostStamp = { display: "inline-flex", alignItems: "center", minHeight: 22, padding: "0 8px", borderRadius: 999, background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca", fontSize: 11, fontWeight: 700 };

// popup styles
const detailOverlay = { position: "fixed", inset: 0, zIndex: 80, background: "rgba(15,23,42,0.62)", display: "flex", alignItems: "flex-end", justifyContent: "center" };
const detailSheet = { width: "min(428px, 100%)", maxHeight: "84vh", overflowY: "auto", background: "#f1f5f9", borderRadius: "22px 22px 0 0", padding: "10px 16px 32px", boxSizing: "border-box" };
const sheetHandle = { width: 44, height: 5, borderRadius: 999, background: "#cbd5e1", margin: "0 auto 14px" };
const ticketHeader = { background: "linear-gradient(135deg, #1e1b4b 0%, #4338ca 100%)", borderRadius: 16, padding: "16px 18px 18px", marginBottom: 14, position: "relative" };
const betedBadge = { display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 999, padding: "4px 12px", fontSize: 11, fontWeight: 900, color: "#facc15", letterSpacing: "0.08em" };
const closeBtn = { width: 30, height: 30, border: "1px solid rgba(255,255,255,0.2)", borderRadius: "50%", background: "rgba(255,255,255,0.1)", display: "grid", placeItems: "center", cursor: "pointer" };
const statusBannerBox = (status) => {
  const v = String(status || "pending").toLowerCase();
  return { display: "flex", alignItems: "center", gap: 12, marginBottom: 14, padding: "12px 16px", borderRadius: 14, background: v === "won" ? "linear-gradient(135deg,#dcfce7,#bbf7d0)" : v === "lost" ? "linear-gradient(135deg,#fee2e2,#fecaca)" : "linear-gradient(135deg,#fef9c3,#fde68a)", border: `1.5px solid ${v === "won" ? "#86efac" : v === "lost" ? "#fca5a5" : "#fcd34d"}` };
};
const gameTypePill = { padding: "7px 16px", borderRadius: 999, background: "linear-gradient(135deg, #4f46e5, #7c3aed)", color: "#fff", fontSize: 12, fontWeight: 800 };
const numSection = { background: "#fff", borderRadius: 14, padding: "12px 14px", marginBottom: 14, border: "1.5px solid #e8eaf6" };
const numSectionLabel = { fontSize: 10, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 };
const numRow = { display: "flex", alignItems: "center", gap: 12, background: "#f8fafc", border: "1px solid #e8eaf6", borderRadius: 12, padding: "10px 12px" };
const numBubble = { minWidth: 52, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #4f46e5, #7c3aed)", color: "#fff", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 900, padding: "0 8px", flexShrink: 0, whiteSpace: "nowrap" };
const numStatChip = (status) => { const v = String(status || "pending").toLowerCase(); return { padding: "4px 8px", borderRadius: 8, fontSize: 10, fontWeight: 800, background: v === "won" ? "#dcfce7" : v === "lost" ? "#fee2e2" : "#fef9c3", color: v === "won" ? "#166534" : v === "lost" ? "#dc2626" : "#92400e", whiteSpace: "nowrap", flexShrink: 0 }; };
const totalsBand = { background: "linear-gradient(135deg, #1e1b4b, #312e81)", borderRadius: 16, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 };
const detailHeader = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 };
const closeButton = { width: 36, height: 36, border: 0, borderRadius: "50%", background: "#f1f5f9", color: "#64748b", display: "grid", placeItems: "center" };
const pillRow = { display: "flex", gap: 10, marginBottom: 16 };
const statusBadge = (status) => {
  const value = String(status || "pending").toLowerCase();
  return {
    padding: "9px 14px",
    borderRadius: 999,
    border: `1px solid ${value === "won" ? "#bbf7d0" : value === "lost" ? "#fecaca" : "#fde68a"}`,
    background: value === "won" ? "#dcfce7" : value === "lost" ? "#fee2e2" : "#fef9c3",
    color: value === "won" ? "#15803d" : value === "lost" ? "#dc2626" : "#b45309",
    fontWeight: 900,
    fontSize: 12,
  };
};
const selectedBox = { background: "#eef2ff", border: "1.5px solid #c7d2fe", borderRadius: 10, padding: 14, display: "grid", gap: 10 };
const selectedTitle = { color: "#4f46e5", fontSize: 12, fontWeight: 800 };
const numberDetail = { background: "#fff", border: "1px solid #e0e7ff", borderRadius: 9, padding: 10, display: "flex", alignItems: "center", gap: 12 };
const wideNumberChip = { minWidth: 56, maxWidth: 140, minHeight: 38, borderRadius: 9, display: "grid", placeItems: "center", padding: "0 10px", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontSize: 14, fontWeight: 900, whiteSpace: "nowrap" };
const miniStatus = (status) => ({ display: "inline-block", marginTop: 5, padding: "4px 7px", borderRadius: 6, background: "#fef3c7", color: "#92400e", fontSize: 10, fontWeight: 800 });
const itemTimeStamp = { marginTop: 6, color: "#64748b", fontSize: 12, fontWeight: 600 };
const detailTotal = { borderTop: "1px solid #e5e7eb", marginTop: 18, paddingTop: 14, display: "flex", justifyContent: "space-between", color: "#334155", fontSize: 18 };
const detailWinRow = { marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12, padding: "10px 12px", color: "#166534", fontSize: 15, fontWeight: 900 };
const bidIdLine = { marginTop: 18, display: "flex", justifyContent: "space-between", color: "#475569", fontSize: 12 };

export default Bids;
